/** Must equal the clip counts the packer wrote (tools/asset-manifest.mjs): a count too high is a cue that intermittently does not play, since a variant past the end is silence, not an error. */
export const SOUND_CATEGORIES = {
  collectScrap: { count: 4 },
  destruction: { count: 10 },
  destructionSelf: { count: 3 },
  destructionSelf2: { count: 6 },
  entitySpawn: { count: 8 },
  powerDown: { count: 2 },
  powerUp: { count: 2 },
  shots: { count: 7 },
  spawn: { count: 8 },
  upgrade: { count: 4 },
  upgradeShip: { count: 8 },
} as const satisfies Record<string, { readonly count: number }>;

export type SoundCategory = keyof typeof SOUND_CATEGORIES;

export const SOUND_CATEGORY_NAMES = Object.keys(SOUND_CATEGORIES) as readonly SoundCategory[];

export function soundClipName(category: SoundCategory, variant: number): string {
  return `${category}/${String(variant)}`;
}

export function pickVariant(category: SoundCategory, unit: number): number {
  const { count } = SOUND_CATEGORIES[category];
  return Math.min(count, Math.floor(unit * count) + 1);
}

export interface SoundCue {
  readonly category: SoundCategory;
  readonly gain: number;
  readonly x: number | null;
  readonly y: number | null;
  readonly cap: number;
}

export interface SoundListener {
  readonly x: number;
  readonly y: number;
  readonly viewDist: number;
}

export function localCue(category: SoundCategory, gain: number): SoundCue {
  return { category, gain, x: null, y: null, cap: 1 };
}

export function positionalCue(
  category: SoundCategory,
  gain: number,
  x: number,
  y: number,
  cap = 1,
): SoundCue {
  return { category, gain, x, y, cap };
}

/** The floor, not zero: a sound at this volume is inaudible but still starts, as it always did. Past half the view distance the ramp collapses to this rather than fading to nothing. */
const INAUDIBLE = 0.0001;

export function attenuation(distance: number, viewDist: number): number {
  if (Number.isNaN(distance)) return INAUDIBLE;
  const volume = 0.1 + (1 - distance / (viewDist / 2)) * 0.9;
  return volume > 0 && volume <= 1 ? volume : INAUDIBLE;
}

export function cueVolume(cue: SoundCue, listener: SoundListener): number {
  if (cue.x === null || cue.y === null) return cue.gain;
  const dx = cue.x - listener.x;
  const dy = cue.y - listener.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return cue.gain * Math.min(attenuation(distance, listener.viewDist), cue.cap);
}
